import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { SparkRenderer, SplatMesh, SplatFileType } from "@sparkjsdev/spark";

const SEV_COLOR = { critical: 0xff3b30, high: 0xff9500, medium: 0xffcc00, low: 0x34c759 };

// Standalone Three.js + Spark Gaussian Splat canvas. Fully separate from the
// Matterport iframe — additive, no shared state.
export default function SplatViewer({ url, fileType, markers = [], transform, onLoaded, onError }) {
  const mountRef = useRef(null);
  const stateRef = useRef({});

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);
    // Spark requires a SparkRenderer added to the scene to actually draw splats.
    const spark = new SparkRenderer({ renderer });
    scene.add(spark);
    const camera = new THREE.PerspectiveCamera(60, mount.clientWidth / mount.clientHeight, 0.01, 2000);
    camera.position.set(0, 0, 3);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    const markersGroup = new THREE.Group();
    scene.add(markersGroup);

    let raf;
    const animate = () => { controls.update(); renderer.render(scene, camera); raf = requestAnimationFrame(animate); };
    animate();

    const onResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    stateRef.current = { renderer, scene, camera, controls, markersGroup, spark, splat: null };
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      controls.dispose();
      try { stateRef.current.splat?.dispose?.(); } catch (e) {}
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []);

  // Load / reload the splat when the source changes.
  useEffect(() => {
    const st = stateRef.current;
    if (!st.scene || !url) return;
    let disposed = false;
    if (st.splat) { try { st.scene.remove(st.splat); st.splat.dispose?.(); } catch (e) {} st.splat = null; }
    try {
      const opts = { url };
      if (fileType && SplatFileType[fileType]) opts.fileType = SplatFileType[fileType];
      const splat = new SplatMesh(opts);
      // Bring the splat upright: -90° about X maps the file's Y-down/Z-forward
      // convention to THREE's Y-up world so we view the top, not the underside.
      splat.quaternion.setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
      const done = splat.initialized instanceof Promise ? splat.initialized : Promise.resolve();
      done.then(() => {
        if (disposed) return;
        st.scene.add(splat);
        st.splat = splat;
        frameObject(st.camera, st.controls, splat);
        onLoaded?.();
      }).catch((e) => { if (!disposed) onError?.(e); });
    } catch (e) { onError?.(e); }
    return () => { disposed = true; };
  }, [url, fileType]);

  // Rebuild markers when the defect set changes.
  useEffect(() => {
    const g = stateRef.current.markersGroup;
    if (!g) return;
    while (g.children.length) { const c = g.children.pop(); c.geometry?.dispose?.(); c.material?.dispose?.(); }
    markers.forEach((m) => {
      if (!m.position || m.position.x == null) return;
      const s = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 16, 16),
        new THREE.MeshBasicMaterial({ color: SEV_COLOR[m.severity] || 0xffcc00, depthTest: false })
      );
      s.renderOrder = 999;
      s.position.set(m.position.x, m.position.y, m.position.z);
      g.add(s);
    });
  }, [markers]);

  // Apply the alignment transform to the marker group (Matterport-space -> splat-space).
  useEffect(() => {
    const g = stateRef.current.markersGroup;
    if (!g || !transform) return;
    g.position.set(transform.tx, transform.ty, transform.tz);
    g.rotation.set(
      THREE.MathUtils.degToRad(transform.rx),
      THREE.MathUtils.degToRad(transform.ry),
      THREE.MathUtils.degToRad(transform.rz)
    );
    g.scale.setScalar(transform.scale || 1);
  }, [transform]);

  return <div ref={mountRef} className="w-full h-full" data-testid="splat-canvas" />;
}

function frameObject(camera, controls, obj) {
  try {
    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3()).length();
    const center = box.getCenter(new THREE.Vector3());
    if (!isFinite(size) || size === 0) return;
    controls.target.copy(center);
    camera.position.copy(center).add(new THREE.Vector3(0, size * 0.15, size * 0.7 + 0.5));
    camera.near = Math.max(size / 1000, 0.001);
    camera.far = size * 12;
    camera.updateProjectionMatrix();
    controls.update();
  } catch (e) { /* keep default framing */ }
}
